import { Component, ErrorInfo, ReactNode } from 'react'

interface Props {
  /** 크래시한 영역 이름(에러 메시지에 표기) — 예: "속성 시트", "가이드". */
  label: string
  /** "닫기"를 눌렀을 때 부모가 해당 영역을 접도록(예: 시트 닫기) 호출된다. */
  onReset?: () => void
  children: ReactNode
}

interface State {
  error: Error | null
}

/**
 * EditorErrorBoundary — 에디터 서브트리(속성 시트·가이드 등)를 감싸, 렌더 중
 * 예외가 나도 **에디터 전체(맵 캔버스)가 하얗게 사라지지 않게** 격리한다.
 *
 * 에디터는 iframe 안에서 라우트로 렌더되는데 상위(MapEditorErrorBoundary)는
 * iframe 바깥만 감싸므로, 내부에서 잡히지 않은 렌더 예외가 나면 iframe 전체가
 * 하얀 화면이 된다. 이 바운더리가 크래시한 조각만 대체 UI로 바꿔 나머지 화면을
 * 살리고, 원인 파악을 위해 에러 메시지를 그대로 노출한다.
 */
export class EditorErrorBoundary extends Component<Props, State> {
  state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // 콘솔에도 남겨 스택으로 근본 원인을 추적할 수 있게 한다.
    console.error(`[EditorErrorBoundary:${this.props.label}]`, error, info.componentStack)
  }

  private handleReset = () => {
    this.setState({ error: null })
    this.props.onReset?.()
  }

  render() {
    const { error } = this.state
    if (!error) return this.props.children

    return (
      <div
        role="alert"
        style={{
          padding: 16,
          margin: 12,
          border: '1px solid #f5c2c7',
          background: '#fff5f5',
          borderRadius: 8,
          color: '#842029',
          maxWidth: 480,
          fontSize: 13,
          lineHeight: 1.5,
        }}
      >
        <strong>⚠️ {this.props.label}에서 오류가 발생했어요</strong>
        <div style={{ marginTop: 8, fontFamily: 'monospace', wordBreak: 'break-word' }}>
          {error.message || String(error)}
        </div>
        <button
          type="button"
          onClick={this.handleReset}
          style={{
            marginTop: 12,
            padding: '6px 12px',
            border: '1px solid #842029',
            background: 'transparent',
            color: '#842029',
            borderRadius: 6,
            cursor: 'pointer',
          }}
        >
          닫기
        </button>
      </div>
    )
  }
}
